import { type FormEvent, useEffect, useState, useCallback } from 'react';
import type { Country, MyAccountAddress } from '../MyAccount.types';

interface AddressFormProps {
  address: MyAccountAddress | null;
  countries: Country[];
  getStatesUrl: string;
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isPending: boolean;
  errors?: Record<string, string>;
  serverError?: string | null;
}

export const AddressForm = ({
  address,
  countries,
  getStatesUrl,
  onSave,
  onCancel,
  isPending,
  errors: externalErrors,
  serverError,
}: AddressFormProps) => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>(externalErrors ?? {});

  const clearError = (field: string) => {
    if (fieldErrors[field]) setFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  };

  const inputClass = (field: string) => `ma-input${fieldErrors[field] ? ' has-error' : ''}`;
  const [name, setName] = useState(address?.name ?? '');
  const [companyName, setCompanyName] = useState(address?.companyName ?? '');
  const [line1, setLine1] = useState(address?.addressLine1 ?? '');
  const [line2, setLine2] = useState(address?.addressLine2 ?? '');
  const [city, setCity] = useState(address?.addressLine3 ?? '');
  const [zipCode, setZipCode] = useState(address?.zipCode ?? '');
  const [countryCode, setCountryCode] = useState(address?.countryCode ?? 'IRL');
  const [stateCode, setStateCode] = useState(address?.stateCode ?? '');
  const [phone, setPhone] = useState(address?.phone ?? '');
  const [states, setStates] = useState<{ code: string; name: string }[]>([]);

  const fetchStates = useCallback(async (code: string) => {
    try {
      const res = await fetch(`${getStatesUrl}?countryCode=${code}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      setStates(
        Array.isArray(data)
          ? data.map((s: { id: string; name: string }) => ({ code: s.id, name: s.name }))
          : [],
      );
    } catch {
      setStates([]);
    }
  }, [getStatesUrl]);

  useEffect(() => {
    if (countryCode) {
      fetchStates(countryCode);
    }
  }, []);

  const handleCountryChange = (code: string) => {
    setCountryCode(code);
    setStateCode('');
    fetchStates(code);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      addressId: address?.addressId ?? null,
      fullName: name,
      companyName,
      streetAddress: line1,
      streetAddress2: line2,
      city,
      postCode: zipCode,
      countryCode,
      stateId: stateCode,
      contactNumber: phone,
    });
  };

  return (
    <div className="ma-card">
      <form onSubmit={handleSubmit}>
        {serverError ? <p className="ma-server-error" role="alert">{serverError}</p> : null}
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-name">Full name</label>
          <input id="addr-name" className={inputClass('fullName')} type="text" value={name} onChange={e => { setName(e.target.value); clearError('fullName'); }} required />
          {fieldErrors.fullName && <span className="ma-field-error">{fieldErrors.fullName}</span>}
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-company">Company (optional)</label>
          <input id="addr-company" className={inputClass('companyName')} type="text" value={companyName} onChange={e => { setCompanyName(e.target.value); clearError('companyName'); }} />
          {fieldErrors.companyName && <span className="ma-field-error">{fieldErrors.companyName}</span>}
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-line1">Address line 1</label>
          <input id="addr-line1" className={inputClass('streetAddress')} type="text" value={line1} onChange={e => { setLine1(e.target.value); clearError('streetAddress'); }} required />
          {fieldErrors.streetAddress && <span className="ma-field-error">{fieldErrors.streetAddress}</span>}
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-line2">Address line 2 (optional)</label>
          <input id="addr-line2" className="ma-input" type="text" value={line2} onChange={e => setLine2(e.target.value)} />
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-city">City / Town</label>
          <input id="addr-city" className={inputClass('city')} type="text" value={city} onChange={e => { setCity(e.target.value); clearError('city'); }} required />
          {fieldErrors.city && <span className="ma-field-error">{fieldErrors.city}</span>}
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-zip">Postcode / Eircode</label>
          <input id="addr-zip" className={inputClass('postCode')} type="text" value={zipCode} onChange={e => { setZipCode(e.target.value); clearError('postCode'); }} required />
          {fieldErrors.postCode && <span className="ma-field-error">{fieldErrors.postCode}</span>}
        </div>
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-country">Country</label>
          <select id="addr-country" className="ma-input" value={countryCode} onChange={e => handleCountryChange(e.target.value)}>
            {countries.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
        {states.length > 0 && (
          <div className="ma-field">
            <label className="ma-label" htmlFor="addr-state">County / State</label>
            <select id="addr-state" className="ma-input" value={stateCode} onChange={e => setStateCode(e.target.value)}>
              <option value="">Select...</option>
              {states.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="ma-field">
          <label className="ma-label" htmlFor="addr-phone">Phone number</label>
          <input id="addr-phone" className={inputClass('contactNumber')} type="tel" value={phone} onChange={e => { setPhone(e.target.value); clearError('contactNumber'); }} />
          {fieldErrors.contactNumber && <span className="ma-field-error">{fieldErrors.contactNumber}</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
          <button type="submit" className="ma-btn-primary" style={{ marginTop: 0 }} disabled={isPending}>
            {address ? 'Update address' : 'Add address'}
          </button>
          <button type="button" className="ma-btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
};
